/**
 * Renders the small subset of Markdown the assistant actually produces (bold, numbered lines,
 * bullet lines, paragraphs) as HTML, for binding via [innerHTML].
 *
 * Safe by construction, not by sanitizing afterward: every character of the input is HTML-escaped
 * first, so the only tags that can ever appear in the output are the fixed handful this function
 * emits itself (<p>, <strong>, <div>, <span>). There is no path from model output — or, transitively,
 * from tool-result content a prompt injection might try to influence — to an actual HTML tag,
 * regardless of what text comes in.
 */
export function formatAssistantMarkdown(text: string): string {
    if (!text) {
        return '';
    }

    const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const applyInline = (value: string): string => escapeHtml(value).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    const lines = text.split('\n');
    const htmlParts: string[] = [];
    let bulletBuffer: string[] = [];

    const flushBullets = (): void => {
        if (!bulletBuffer.length) {
            return;
        }
        const items = bulletBuffer.map((item) => `<div class="flex gap-1.5"><span>&bull;</span><span>${item}</span></div>`).join('');
        htmlParts.push(`<div class="flex flex-col gap-0.5 pl-4">${items}</div>`);
        bulletBuffer = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            flushBullets();
            continue;
        }

        const bulletMatch = line.match(/^[-*]\s+(.*)$/);
        const numberedMatch = line.match(/^(\d+)\.\s+(.*)$/);

        if (bulletMatch) {
            bulletBuffer.push(applyInline(bulletMatch[1]));
        } else if (numberedMatch) {
            flushBullets();
            htmlParts.push(`<p><strong>${numberedMatch[1]}.</strong> ${applyInline(numberedMatch[2])}</p>`);
        } else {
            flushBullets();
            htmlParts.push(`<p>${applyInline(line)}</p>`);
        }
    }
    flushBullets();

    return htmlParts.join('');
}
